/**
 * NotFoundPage — 404 with a calm, on-brand illustration and a "go home" CTA.
 *
 * Light theme only. Linear / Notion-style minimalism: large numeral, soft
 * supporting illustration, single primary action, secondary "go back".
 */
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Home } from "lucide-react";

import { Button } from "@/components/ui/Button";

export function NotFoundPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div
      role="main"
      className="flex flex-1 items-center justify-center bg-surface-base p-8"
    >
      <div className="grid w-full max-w-3xl grid-cols-1 items-center gap-10 md:grid-cols-2">
        <Illustration />

        <div className="flex flex-col gap-4 text-center md:text-left">
          <p className="text-xs font-semibold uppercase tracking-widest text-brand-700">
            {t("errors.notFound")}
          </p>
          <h1 className="text-3xl font-bold leading-tight text-fg-primary md:text-4xl">
            お探しのページは
            <br />
            見つかりませんでした。
          </h1>
          <p className="text-sm text-fg-secondary md:text-base">
            URL が変更されたか、削除された可能性があります。
            ホームに戻るか、左上の検索 (Ctrl+K) で目的のページをお探しください。
          </p>

          <div className="mt-2 flex flex-wrap justify-center gap-2 md:justify-start">
            <Link to="/">
              <Button variant="primary" leadingIcon={<Home size={16} aria-hidden />}>
                {t("nav.home")}
              </Button>
            </Link>
            <Button
              variant="secondary"
              leadingIcon={<ArrowLeft size={16} aria-hidden />}
              onClick={() => navigate(-1)}
            >
              一つ前に戻る
            </Button>
          </div>

          <p className="mt-6 text-xs text-fg-muted">
            Error 404 — Page not found
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Calm, on-brand illustration. Pure SVG so it scales cleanly and adds no JS
 * weight. Subtle rings + the numeral "404" rendered as the focal element.
 */
function Illustration(): JSX.Element {
  return (
    <svg
      viewBox="0 0 360 240"
      className="mx-auto w-full max-w-sm"
      role="img"
      aria-label="404 illustration"
    >
      <defs>
        <linearGradient id="nf-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#dbeafe" />
          <stop offset="100%" stopColor="#eff6ff" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="360" height="240" rx="16" fill="url(#nf-grad)" />
      {/* Soft rings */}
      <circle cx="180" cy="120" r="92" fill="none" stroke="#bfdbfe" strokeWidth="1.5" />
      <circle cx="180" cy="120" r="64" fill="none" stroke="#93c5fd" strokeWidth="1.5" strokeDasharray="3 4" />
      {/* 404 numeral */}
      <text
        x="180"
        y="148"
        textAnchor="middle"
        fontSize="80"
        fontWeight="800"
        fill="#1e3a8a"
        fontFamily="Inter, 'Noto Sans JP', system-ui, sans-serif"
      >
        404
      </text>
      {/* Floating dots */}
      <circle cx="60" cy="60" r="4" fill="#1e3a8a" opacity="0.35" />
      <circle cx="300" cy="48" r="3" fill="#1e3a8a" opacity="0.25" />
      <circle cx="312" cy="190" r="5" fill="#1e3a8a" opacity="0.3" />
      <circle cx="48" cy="200" r="3" fill="#1e3a8a" opacity="0.35" />
    </svg>
  );
}
